import type { ReactNode } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

import type { OrganizationMember } from './members.interface'

interface MemberNameCellProps {
  member: OrganizationMember
}

const getInitials = (firstName: string | null, lastName: string | null, email: string): string => {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase()
  }
  if (firstName) {
    return firstName.slice(0, 2).toUpperCase()
  }
  return email.slice(0, 2).toUpperCase()
}

const getDisplayName = (member: OrganizationMember): string => {
  if (member.firstName && member.lastName) {
    return `${member.firstName} ${member.lastName}`
  }
  if (member.firstName) {
    return member.firstName
  }
  return member.email
}

export const MemberNameCell: React.FC<MemberNameCellProps> = ({ member }): ReactNode => {
  const initials = getInitials(member.firstName, member.lastName, member.email)
  const displayName = getDisplayName(member)

  return (
    <div className="member-name-cell flex items-center gap-3">
      <Avatar className="size-8">
        <AvatarImage src={member.imageUrl ?? undefined} alt={displayName} />
        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col">
        <span className="font-medium">
          {displayName}
          {member.isCurrentUser && <span className="ml-1 text-muted-foreground">(You)</span>}
        </span>
        {member.firstName && <span className="text-xs text-muted-foreground">{member.email}</span>}
      </div>
    </div>
  )
}

MemberNameCell.displayName = 'MemberNameCell'
